import axios from "axios";
import { getAnonymousVisitorId } from "../utils/anonymousVisitor";

axios.defaults.xsrfCookieName = "csrf_token";
axios.defaults.xsrfHeaderName = "x-csrf-token";
axios.defaults.withCredentials = true;

// Cross-origin CSRF helper: Axios doesn't automatically send the XSRF header for cross-origin requests.
// Since our frontend and backend are often on different Railway domains, we need to manually inject it.
function getCsrfFromCookie() {
  const name = "csrf_token=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(name) === 0) return c.substring(name.length, c.length);
  }
  return "";
}

function getCsrfToken() {
  // Try cookie first (same-origin), fall back to localStorage (cross-origin)
  const fromCookie = getCsrfFromCookie();
  if (fromCookie) return fromCookie;
  return localStorage.getItem("csrf_token") || "";
}

axios.interceptors.request.use((config) => {
  const token = getCsrfToken();
  if (token && ["post", "put", "patch", "delete"].includes(config.method?.toLowerCase())) {
    config.headers["x-csrf-token"] = token;
  }
  const anonymousVisitorId = getAnonymousVisitorId();
  if (anonymousVisitorId) {
    config.headers["x-anonymous-id"] = anonymousVisitorId;
  }
  
  // Inject workspace and project IDs if active
  const workspaceId = localStorage.getItem("currentWorkspaceId");
  const projectId = localStorage.getItem("currentProjectId");
  if (workspaceId) {
    config.headers["x-workspace-id"] = workspaceId;
  }
  if (projectId) {
    config.headers["x-project-id"] = projectId;
  }
  return config;
});

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token) {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    if (status === 401 && originalRequest && !originalRequest._retry && !originalRequest.url.includes("/api/auth/refresh")) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers["x-auth-token"] = token;
            resolve(axios(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const hasAccessToken = !!localStorage.getItem("token");
      if (!hasAccessToken) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        // Must send CSRF token header + auth token so enforceCsrf passes
        const csrfToken = getCsrfToken();
        const res = await axios.post(apiUrl("/api/auth/refresh"), undefined, {
          headers: {
            ...getAuthHeaders(),
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          withCredentials: true,
        });
        const newToken = res.data?.data?.token || res.data?.token;
        if (newToken) {
          localStorage.setItem("token", newToken);
          isRefreshing = false;
          onRefreshed(newToken);
          originalRequest.headers["x-auth-token"] = newToken;
          return axios(originalRequest);
        }
        // No token in response — treat as failure
        isRefreshing = false;
      } catch (refreshError) {
        isRefreshing = false;
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("refreshTokenId");
        window.location.href = "/login"; // Force re-login
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);


export function getApiBaseUrl() {
  const raw = (process.env.REACT_APP_API_URL || "").trim();
  if (!raw) {
    throw new Error("REACT_APP_API_URL environment variable is mandatory for the frontend to connect to the backend.");
  }
  const baseUrl = raw;
  const noTrailing = baseUrl.replace(/\/+$/, "");
  return noTrailing.replace(/\/api$/i, "");
}

const BASE = getApiBaseUrl();

function unwrapApiEnvelope(response) {
  const body = response?.data;
  if (body && typeof body === "object" && body.status === "SUCCESS") {
    if ("results" in body) {
      return { ...response, data: body.results };
    }
    if ("data" in body) {
      return { ...response, data: body.data };
    }
  }
  return response;
}

export function getAuthHeaders() {
  const token = localStorage.getItem("token");
  const headers = token ? { "x-auth-token": token } : {};
  const workspaceId = localStorage.getItem("currentWorkspaceId");
  const projectId = localStorage.getItem("currentProjectId");
  if (workspaceId) headers["x-workspace-id"] = workspaceId;
  if (projectId) headers["x-project-id"] = projectId;
  return headers;
}

export function apiUrl(path) {
  return `${BASE}${path}`;
}

export async function apiGet(path, params = {}) {
  const response = await axios.get(apiUrl(path), { params, headers: getAuthHeaders() });
  return unwrapApiEnvelope(response);
}

export async function apiPost(path, body) {
  const response = await axios.post(apiUrl(path), body, { headers: getAuthHeaders() });
  return unwrapApiEnvelope(response);
}

export async function apiPut(path, body) {
  const response = await axios.put(apiUrl(path), body, { headers: getAuthHeaders() });
  return unwrapApiEnvelope(response);
}

// BUG FIX: apiPatch was missing from the exports. Schedules.js and
// useSchedules.js both need it, and the old workaround in Schedules.js was
// importing axios directly and re-implementing the function inline — without
// the auth header helper — causing 401 errors when toggling a schedule.
export async function apiPatch(path, body) {
  const response = await axios.patch(apiUrl(path), body, { headers: getAuthHeaders() });
  return unwrapApiEnvelope(response);
}

export async function apiDelete(path) {
  const response = await axios.delete(apiUrl(path), { headers: getAuthHeaders() });
  return unwrapApiEnvelope(response);
}

export async function apiGetBlob(path) {
  return axios.get(apiUrl(path), {
    headers: getAuthHeaders(),
    responseType: "blob",
  });
}
